import cn from 'classnames';
import { Fragment, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import { sendInvitation } from 'actions/user';
import FavoriteIconTooltip from 'components/common/FavoriteIconTooltip';
import MealCardTitle from 'components/common/MealCardTitle';
import ReviewContainer from 'components/common/ReviewContainer';
import ReviewModalContainer from 'components/common/ReviewModalContainer';
import ToastMessage from 'components/common/ToastMessage';
import MealBadgeIcon from 'components/icons/MealBadgeIcon';
import { TEMPO } from 'context/BrandContext';
import * as Routes from 'helpers/routes';
import { displayServingsText } from 'helpers/servings';
import useMealImageToDisplay from 'hooks/useMealImageToDisplay';
import { TEMPO_TAGGED_REVIEWS } from 'lib/constants/experimentConstants';
import * as fromRoot from 'reducers';
import * as fromDashboard from 'selectors/dashboard';
import * as Analytics from 'services/analytics';
import style from 'styles/shared/dashboardMealCardRedesign';
import * as Types from 'utilities/types';

export const MealCard = ({
  meal,
  mealOptionSelection,
  mealPlanSlug,
  menuId,
  menuSlug,
  review,
  reviewReasons,
  compressMealCard = false,
  isFavorite,
  isSkippedBasket = false,
  linkPath = Routes.dashboardRootPath,
  isInTempoTaggedReviewsExperiment = false,
  disableLink = false,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRating, setSelectedRating] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const isTempo = mealPlanSlug === TEMPO;
  const checkReviewReasons =
    isInTempoTaggedReviewsExperiment && isTempo ? reviewReasons.length > 0 : true;
  const { id, title, servings, displayUnit, isDonation, mealBadge, subtitle, spiceLevel } = meal;
  const photo = useMealImageToDisplay(meal);

  const transitionRef = useRef();
  const LinkOrFragment = isDonation || disableLink ? Fragment : Link;
  const linkProps = {
    to: linkPath({ mealId: id, menuSlug, title }),
    className: style.cardChild,
  };
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleShowToast = () => {
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
    }, 5000);
  };

  return (
    <article
      className={cn(style.mealCard, {
        [style.compressed]: compressMealCard,
        [style.expanded]: !compressMealCard,
      })}
    >
      <ToastMessage type="success" content="Thanks for your feedback!" visible={showToast} />
      <div className={style.cardChild}>
        <TransitionGroup className="h-full">
          <CSSTransition timeout={200} classNames="fadeInOut" nodeRef={transitionRef}>
            <LinkOrFragment {...(isDonation ? {} : linkProps)}>
              {mealBadge && (
                <MealBadgeIcon
                  mealBadge={mealBadge}
                  containerClass="!h-12 !w-12 bp2:!h-16 bp2:!w-16"
                />
              )}
              <img
                width="100%"
                alt={title}
                src={photo}
                className={cn(style.mealCardImg, isHovered ? style.shadow : '')}
              />

              <div className="mb-2 text-charcoal-90">
                {isFavorite && (
                  <FavoriteIconTooltip
                    ariaId="dashboard-meal-card-favorite"
                    containerClassname="-mt-7 bp2:-mt-8"
                    iconClassname="h-6 w-6 bp2:h-8 bp2:w-8"
                  />
                )}
                <div
                  className="pb-1 pt-3 text-charcoal-100"
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  <MealCardTitle
                    meal={{ title, subtitle, spiceLevel }}
                    mealPlanSlug={mealPlanSlug}
                    showSubtitle={false}
                    titleClassName="textMd bp2:h5"
                  />
                  {!compressMealCard && mealOptionSelection.length > 0 && (
                    <div className="w-[200px] bp2:w-[300px]">
                      <p
                        key={mealOptionSelection[0].mealOptionId}
                        className={cn(style.customizeItText, 'textSm text-charcoal-90')}
                      >
                        sub w/ {mealOptionSelection[0].displayName}
                      </p>
                    </div>
                  )}
                </div>
                {!compressMealCard && (
                  <div>
                    {!isSkippedBasket && servings && (
                      <div
                        className={cn('text text-charcoal-90')}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                      >
                        <p>{displayServingsText({ servings, displayUnit })}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </LinkOrFragment>
          </CSSTransition>
        </TransitionGroup>
        {review && checkReviewReasons && (
          <ReviewContainer
            mealId={id}
            menuId={menuId}
            setSelectedRating={setSelectedRating}
            setIsModalOpen={setIsModalOpen}
            review={review}
            mealTitle={title}
            ratingSubmittedEvent={Analytics.events.dashboard.ratingSubmitted}
          />
        )}
      </div>
      {review && isModalOpen && (
        <ReviewModalContainer
          existingReview={review}
          isModalOpen={isModalOpen}
          shouldShowTempoTaggedReviewModal={isTempo && isInTempoTaggedReviewsExperiment}
          title={title}
          mealId={id}
          menuId={menuId}
          photoSrc={photo}
          onClose={handleCloseModal}
          onShowToast={handleShowToast}
          initialRating={selectedRating}
          ratingSubmittedEvent={Analytics.events.dashboard.ratingSubmitted}
          reviewSubmittedEvent={Analytics.events.dashboard.reviewSubmitted}
        />
      )}
    </article>
  );
};

const mapStateToProps = (state, { meal: { id }, menuSlug }) => {
  const basket = fromDashboard.selectBasket(state, menuSlug);
  const menuId = basket?.menu?.id;
  const mealPlanSlug = basket?.mealPlan;

  return {
    menuId,
    mealPlanSlug,
    review: fromDashboard.selectReview(state, id),
    isInTempoTaggedReviewsExperiment: fromRoot.selectExperimentAssignment(
      state,
      TEMPO_TAGGED_REVIEWS
    ).isVariant,
    reviewReasons: fromDashboard.selectReviewReasonList(state),
  };
};

const mapDispatchToProps = {
  onSendInvitation: sendInvitation,
};

MealCard.propTypes = {
  compressMealCard: PropTypes.bool.isRequired,
  meal: Types.meal.isRequired,
  mealOptionSelection: PropTypes.arrayOf(Types.mealOptionSelection),
  mealPlanSlug: PropTypes.string.isRequired,
  menuId: PropTypes.string.isRequired,
  menuSlug: PropTypes.string.isRequired,
  isFavorite: PropTypes.bool.isRequired,
  review: Types.review,
  selectedMealOptions: PropTypes.objectOf(Types.mealOption),
  isSkippedBasket: PropTypes.bool,
  linkPath: PropTypes.func,
  isInTempoTaggedReviewsExperiment: PropTypes.bool,
  reviewReasons: PropTypes.array,
  disableLink: PropTypes.bool,
};
export default connect(mapStateToProps, mapDispatchToProps)(MealCard);
